import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import styled from 'styled-components';
import { FaPlus, FaTimes } from 'react-icons/fa';

import { DetailFormSelect, DetailFormValue } from 'components/DetailsView';
import { RmeColumnType, GetMicrostoreColumnsOfOrganizationQuery } from 'data/__generated__';

export const RmeConfigurationDetails: React.FC<{
    microstoreColumns: GetMicrostoreColumnsOfOrganizationQuery['microstoreColumnsOfOrganization'];
}> = ({ microstoreColumns }) => {
    const { control, register, watch } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'invoiceSettings.rmeColumns',
    });

    const machineOptions = [
        { value: null, label: 'Toutes les machines' },
        ...microstoreColumns.map((column) => ({
            value: column._id,
            label: column.name,
        })),
    ];

    const typeOptions = [
        { value: RmeColumnType.Transactions, label: 'Transactions' },
        { value: RmeColumnType.Revenue, label: "Chiffre d'affaires" },
    ];

    return (
        <>
            <SectionTitle>Facturation RME</SectionTitle>
            <SectionDescription>
                RME minimum à atteindre : nombre de transactions ou chiffre d'affaires (€).
            </SectionDescription>
            <SectionDescription>
                Transactions : fixer un nombre minimum, chaque transaction peut avoir son prix unitaire. Exemple :
                minimum 1 000 transactions à 1 € = 1 000 €.
            </SectionDescription>
            <SectionDescription>
                Chiffre d'affaires : fixer un montant minimum, nous facturons la différence jusqu'au seuil. Exemple :
                minimum 5 000 € et 4 200 € générés → facturation de 800 €.
            </SectionDescription>
            {fields.map((column, columnIndex) => {
                const rmeType = watch(`invoiceSettings.rmeColumns.${columnIndex}.type`);
                const isTransactionType = rmeType === RmeColumnType.Transactions;

                return (
                    <Card key={column.id}>
                        <MachineSection>
                            <DetailFormSelect
                                isMulti
                                label="Machines"
                                placeholder="Sélectionner une ou plusieurs machines"
                                name={`invoiceSettings.rmeColumns.${columnIndex}.microstoreColumnIds`}
                                options={machineOptions}
                            />
                        </MachineSection>

                        <TypeSection>
                            <DetailFormSelect
                                label="Type RME"
                                placeholder="Sélectionner un type"
                                name={`invoiceSettings.rmeColumns.${columnIndex}.type`}
                                options={typeOptions}
                            />
                        </TypeSection>

                        <AmountSection>
                            <AmountLabel>{isTransactionType ? 'Nb de transactions' : 'Montant minimum'}</AmountLabel>
                            <DetailFormValue
                                type="number"
                                placeholder={isTransactionType ? 'Transactions' : 'Montant (€)'}
                                step="0.0001"
                                min="0"
                                width="120px"
                                {...register(`invoiceSettings.rmeColumns.${columnIndex}.minimumAmount`, {
                                    valueAsNumber: true,
                                })}
                            />
                        </AmountSection>

                        {isTransactionType && (
                            <AmountSection>
                                <AmountLabel>Prix unitaire</AmountLabel>
                                <DetailFormValue
                                    type="number"
                                    placeholder="Prix (€)"
                                    step="0.0001"
                                    min="0"
                                    width="120px"
                                    {...register(`invoiceSettings.rmeColumns.${columnIndex}.unitPrice`, {
                                        valueAsNumber: true,
                                    })}
                                />
                            </AmountSection>
                        )}

                        <DeleteButton type="button" onClick={() => remove(columnIndex)}>
                            <FaTimes />
                        </DeleteButton>
                    </Card>
                );
            })}

            <AddColumnSection>
                <Button
                    type="button"
                    onClick={() =>
                        append({
                            microstoreColumnIds: [],
                            type: RmeColumnType.Transactions,
                            minimumAmount: 0,
                            unitPrice: 0,
                        })
                    }
                >
                    <FaPlus />
                </Button>
            </AddColumnSection>
        </>
    );
};

const Card = styled.div`
    padding: 5px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const Button = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    color: white;
    cursor: pointer;
    flex-shrink: 0;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    font-size: 16px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-left: auto;

    &:hover {
        background-color: ${({ theme }) => `${theme.errorColor}20`};
    }
`;

const MachineSection = styled.section`
    display: flex;
    flex-direction: column;
    width: 260px;
    min-width: 260px;
    margin-bottom: 5px;
`;

const TypeSection = styled.section`
    display: flex;
    flex-direction: column;
    width: 160px;
    min-width: 160px;
`;

const AmountSection = styled.section`
    display: flex;
    flex-direction: column;
`;

const AmountLabel = styled.span`
    width: 130px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
    margin-bottom: 5px;
`;

const AddColumnSection = styled.section`
    margin-bottom: 5px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.textColor};
    font-size: 16px;
    font-weight: 600;
`;

const SectionDescription = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.textColor};
    font-size: 12px;
    opacity: 0.7;
`;
